import { addWarningToast } from "@/components/custom-toast/custom-toast";
import { useModalProvider } from "@/components/modal-popup/modal-popup";
import { BrowserEnvironmentCheckUsecase } from "@/domain/usecase/common/auth/browser-environment-check-usecase";
import { TimeZoneCheckUsecase } from "@/domain/usecase/common/auth/time-zone-check-useacse";
import { FetchUserInfoUsecase } from "@/domain/usecase/common/header/fetch-user-info-usecase";
import { LoginUsecase } from "@/domain/usecase/common/login/login-usecase";
import { ClearUserDataUsecase } from "@/domain/usecase/self-signup/clear-user-data-usecase";
import {
  clearPasswordChangeCookies,
  clearUserRoleCookie,
  setPasswordChangeRequired,
  setUserRoleCookie,
} from "@/utils/cookie-utils";
import { getHomeHref, getPasswordChangeHref, getRoleFromString } from "@/utils/enum/user-role";
import { PasswordChangeType } from "@/utils/global-constants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface LoginViewModelDependencies {
  loginUsecase: LoginUsecase;
  fetchUserInfoUsecase: FetchUserInfoUsecase;
  timeZoneCheckUsecase: TimeZoneCheckUsecase;
  browserEnvironmentCheckUsecase: BrowserEnvironmentCheckUsecase;
}

// 로컬스토리지 키 상수
const REMEMBER_EMAIL_KEY = "rememberedEmail";
const REMEMBER_CHECK_KEY = "remember";

export function useLoginViewModel(deps: LoginViewModelDependencies) {
  // 사용자 입력 상태
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setError] = useState("");
  const [timeZone, setTimeZone] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(true);

  // React Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showModal } = useModalProvider();

  //MARK: Init
  // 브라우저 시간대 설정 및 저장된 이메일 불러오기
  useEffect(() => {
    console.log("[LoginViewModel] :: useEffect init :: 브라우저 시간대 설정");
    // 브라우저의 시간대 정보 가져오기
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimeZone(browserTimeZone);

    // 이전 사용자 정보 및 쿠키 초기화
    clearUserRoleCookie();

    console.log("[LoginViewModel] :: useEffect init :: 이전 사용자 정보 초기화 완료");

    // 저장된 이메일 불러오기
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    const savedRemember = localStorage.getItem(REMEMBER_CHECK_KEY);

    if (savedEmail && savedRemember === "true") {
      setEmail(savedEmail);
      setRemember(true);
    } else {
      setRemember(false);
    }
  }, []);

  // 크롬 또는 엣지 브라우저가 아닌 경우 모달을 띄운다.
  useEffect(() => {
    const browserEnvironmentResult = deps.browserEnvironmentCheckUsecase.execute();
    if (!browserEnvironmentResult.isValid) {
      showModal({
        title: t("login.browserWarning.title"),
        description: [t("login.browserWarning.description")],
        error: null,
        okButton: {
          title: t("login.browserWarning.okButton"),
          onClick: () => {},
        },
      });
    }
  }, []);

  //MARK: Handlers
  /**
   /**
    * 로그인 버튼 클릭 시 호출되는 핸들러
    *
    * 1. 입력한 이메일과 비밀번호로 로그인 요청을 보냅니다.
    * 2. 로그인 성공 시 사용자 정보를 조회합니다. (비밀번호 변경 and 홈 PATH를 사전에 지정하기 위해서)
    * 3. 패스워드 만료 또는 초기 패스워드 상태를 확인 후 필요 시 패스워드 변경 화면으로 이동합니다.
    * 4. 정상 로그인인 경우 초기 패스워드 설정 쿠키를 삭제하고 Role에 맞는 홈으로 이동
    */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // 이전 사용자 정보 초기화 (새로운 로그인 시도 전)
    // deps.clearUserDataUsecase.execute();
    console.log("[LoginViewModel] :: handleSubmit :: 이전 사용자 정보 초기화");

    // 0. 시간대 체크
    const result = await deps.timeZoneCheckUsecase.execute();
    if (result.isFailure) {
      setIsLoading(false);
      showModal({
        title: t("common.timeZoneDetection.title"),
        description: [t("common.timeZoneDetection.description")],
        error: null,
        okButton: {
          title: t("common.ok"),
          onClick: () => {},
        },
      });
      return;
    }

    // 1. 로그인 시도
    const loginResponse = await deps.loginUsecase.execute(email, password);
    console.log("[로그인 응답] 서버 응답:", loginResponse);

    if (loginResponse.isFailure) {
      setIsLoading(false);
      addWarningToast(loginResponse.error);
      return;
    }

    // 로그인 성공 시에만 아이디 저장 (체크되어 있는 경우)
    if (remember && email) {
      localStorage.setItem(REMEMBER_CHECK_KEY, "true");
      localStorage.setItem(REMEMBER_EMAIL_KEY, email);
    }

    // 2. 사용자 정보 조회 (패스워드 만료 여부와 관계없이 먼저 조회)
    // 사용자 롤에 따라서 패스워드를 변경하는 PATH가 달라, 사전에 미리 조회함
    const userInfoResult = await deps.fetchUserInfoUsecase.execute();
    if (userInfoResult.isFailure) {
      console.error("[LoginViewModel] :: handleSubmit :: failure", userInfoResult.error);
      setIsLoading(false);
      addWarningToast(userInfoResult.error);
      return;
    }

    // 사용자 역할 쿠키 설정 (HomeLayout 인증 체크를 위해 필요)
    await setUserRoleCookie(userInfoResult.value.role || "");

    // 패스워드 변경이 필요한 경우 순차적 검사 (초기 패스워드 > 패스워드 만료)
    let passwordChangeType: PasswordChangeType | null = null;

    // 패스워드 만료 검사
    if (loginResponse.value.isExpiredSn) {
      passwordChangeType = PasswordChangeType.EXPIRED;
    }

    if (passwordChangeType) {
      setIsLoading(false);

      // 패스워드 변경 필요 상태를 쿠키에 저장
      setPasswordChangeRequired(passwordChangeType);

      // 사용자 역할에 따라 올바른 패스워드 변경 경로로 네비게이션
      const userRoleString = userInfoResult.value.role;
      const passwordChangeRoute = getPasswordChangeHref(userRoleString);
      navigate(passwordChangeRoute);
      return;
    }

    // 정상 로그인인 경우 패스워드 변경 관련 쿠키 제거
    clearPasswordChangeCookies();

    setIsLoading(false);

    // 3. 홈 경로 설정
    const userRoleString = userInfoResult.value.role;
    let homeRoute = "/home";
    if (userRoleString) {
      try {
        const userRole = getRoleFromString(userRoleString);
        homeRoute = getHomeHref(userRole);
      } catch (error) {
        console.error("[LoginViewModel] :: handleSubmit :: Invalid role:", userRoleString, error);
        // 기본값으로 /home 사용
      }
    }
    console.log(
      "[LoginViewModel] :: handleSubmit :: userRole:",
      userRoleString,
      "homeRoute:",
      homeRoute,
    );
    navigate(homeRoute);
  };

  const handleRemember = () => {
    const newRememberState = !remember;
    setRemember(newRememberState);

    // 체크 시에는 저장하지 않고 로그인 성공 시점에 저장한다.
    if (!newRememberState) {
      // 체크 해제할 때: 저장된 이메일과 remember 상태 제거
      localStorage.removeItem(REMEMBER_CHECK_KEY);
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }
  };

  // 이메일 변경 핸들러
  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail.toLowerCase());
  };

  //MARK: Return
  return {
    email,
    setEmail: handleEmailChange,
    password,
    setPassword,
    errorMsg,
    timeZone,
    isLoading,
    handleSubmit,
    remember,
    handleRemember,
  };
}
